import { MutableRefObject, useEffect, useRef, useState } from 'react';

import { get } from '@eika-infrastruktur/request';

import { getRequestOptions } from '../meldskade/reducer/skademelding-actions';
import { failure, notAsked, RemoteData, success } from '../reducers/remoteData';
import { Skademeldingstype } from '../types/generated-types';
import { BesvarteSporsmalFromCache } from '../types/internal-types';
import { besvarteSporsmalUrl } from '../urler';
import { BesvartSporsmal } from './reducer/current-steg';

export type HTMLHeadingWithTabIndex = HTMLHeadingElement & { focus: () => void };

export type FocusableHtmlElement =
    | HTMLInputElement
    | HTMLTextAreaElement
    | HTMLButtonElement
    | HTMLHeadingWithTabIndex
    | HTMLSelectElement;

export const useFocusRef = <T extends FocusableHtmlElement>(focusOnMount: boolean): MutableRefObject<T> => {
    const inputRef = useRef<T>();

    useEffect(() => {
        if (focusOnMount && inputRef.current) {
            inputRef.current.focus();
        }
    }, [focusOnMount]);

    return inputRef;
};

const isSafari = (): boolean => {
    const chromeAgent = navigator.userAgent.includes('Chrome');
    const safariAgent = navigator.userAgent.includes('Safari');
    if (safariAgent && !chromeAgent) return true;
    return false;
};

export const useWindowScrollTop = (options: ScrollToOptions = { top: 0, left: 0, behavior: 'smooth' }) => {
    const scrollWindowTop = () => {
        if (!isSafari()) window.scrollTo(options);
        else window.scrollTo(0, 0);
    };
    return scrollWindowTop;
};

async function fetchBesvarteSporsmalFromCache(
    kundenummer: string,
    bankregnr: string,
    skademeldingstype: Skademeldingstype,
): Promise<RemoteData<BesvartSporsmal[]>> {
    return get(besvarteSporsmalUrl(skademeldingstype, kundenummer), getRequestOptions(bankregnr))
        .then((besvarteSporsmalFromCache: BesvarteSporsmalFromCache) => {
            if (besvarteSporsmalFromCache.json == null || besvarteSporsmalFromCache.json.length === 0) {
                return failure(null);
            }
            return success(JSON.parse(besvarteSporsmalFromCache.json));
        })
        .catch((e) => failure(e));
}

export const useBesvarteSporsmalFromCache = (
    kundenummer: string,
    bankregnr: string,
    skademeldingstype?: Skademeldingstype,
): RemoteData<BesvartSporsmal[]> => {
    const [besvarteSporsmalFromCache, setBesvarteSporsmalFromCache] =
        useState<RemoteData<BesvartSporsmal[]>>(notAsked());

    useEffect(() => {
        if (kundenummer && skademeldingstype) {
            fetchBesvarteSporsmalFromCache(kundenummer, bankregnr, skademeldingstype).then(
                (besvarteSporsmalFromCache: RemoteData<BesvartSporsmal[]>) => {
                    setBesvarteSporsmalFromCache(besvarteSporsmalFromCache);
                },
            );
        }
    }, [bankregnr, kundenummer, skademeldingstype]);

    return besvarteSporsmalFromCache;
};
